import { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import JobReviewStatusTabs from './components/JobReviewStatusTabs';
import JobReviewFilters from './components/JobReviewFilters';
import JobReviewTable from './components/JobReviewTable';
import EmptyStateJobReview from './components/EmptyStateJobReview';
import { usePaginatedPayRunMemberJobsQuery } from './hooks/usePaginatedPayRunMemberJobsQuery';
import { selectSelectedReviewTab, selectStatusCounts, setSelectedReviewTab } from './confirmJobsSlice';
import { useLazyExportPayRunMemberJobsQuery, useUpdateBulkJobStatusMutation } from './api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import GenericModal from 'components/generic-modal/GenericModal';
import { CrossIconvector } from 'assets/icons';
import { getJobReviewStatusMap } from '../utils';
import { AmountFilterOperation } from 'constants/common';
import DateUtils from 'utils/dateUtils';
import { CustomizeColumnsSource, JobReviewStatus, JobReviewTabStatus } from '../constants';
import { Loader } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../events';
import { PayRunMemberJob } from './confirmJob.model';
import CustomizeColumnsModal, { ColumnConfig } from 'components/customize-columns-modal/CustomizeColumnsModal';
import { useSaveCustomColumnsMutation } from '../api';
import JobReviewTableActions from './components/JobReviewTableActions';
import { NO_TAGS_FILTER_ID, jobSearchTypes } from 'containers/jobs/constant';
import { debounce } from 'utils/commonUtils';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';

interface ConfirmJobsTabProps {
  setJobNavigationFunctions?: (functions: {
    getNextJob: (currentJobId: string) => { jobId: string; status: string };
    getPreviousJob: (currentJobId: string) => { jobId: string; status: string };
  }) => void;
}

const getConfirmMoveJobsSubtitle = (jobCount: number, statusLabel: string): string =>
  `Are you sure you want to move ${jobCount} jobs to ${statusLabel}?`;

const ConfirmJobsTab: FC<ConfirmJobsTabProps> = ({ setJobNavigationFunctions }) => {
  const { payRunId, merchantId, jobId } = useParams();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const statusCounts = useAppSelector(selectStatusCounts);
  const selectedReviewTab = useAppSelector(selectSelectedReviewTab);
  const { isActiveFSMPlusPlan } = useFSMPlanInfo();

  const [selectedJobs, setSelectedJobs] = useState<PayRunMemberJob[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [statusToConfirm, setStatusToConfirm] = useState<string | null>(null);

  // Search states
  const [searchText, setSearchText] = useState('');

  // Filter states in parent component
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [amountFilter, setAmountFilter] = useState({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [scheduleStartDate, setScheduleStartDate] = useState<string | null>(null);
  const [scheduleEndDate, setScheduleEndDate] = useState<string | null>(null);
  const [externalCompanies, setExternalCompanies] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);
  const [createdStartDate, setCreatedStartDate] = useState<string | null>(null);
  const [createdEndDate, setCreatedEndDate] = useState<string | null>(null);
  const [invoiceStatus, setInvoiceStatus] = useState<string[]>([]);
  const [estimateStatus, setEstimateStatus] = useState<string[]>([]);
  const [showCustomizeColumns, setShowCustomizeColumns] = useState(false);

  const {
    isFetching: isFetchingJobs,
    list: jobs,
    totalCount,
    fetchData,
    setQueryParams,
    page,
    queryParams,
    columnConfig,
    searchType,
    setSearchType
  } = usePaginatedPayRunMemberJobsQuery(payRunId, merchantId);

  const [updateBulkJobStatus, { isLoading: isActionInProgress }] = useUpdateBulkJobStatusMutation();
  const [saveCustomColumns, { isLoading: isSavingColumns }] = useSaveCustomColumnsMutation();
  const [exportPayRunMemberJobs] = useLazyExportPayRunMemberJobsQuery();

  // Search handlers
  const initiateSearch = (searchTerm?: string) => {
    setQueryParams(prevQueryParams => ({ ...prevQueryParams, search: searchTerm }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [queryParams]);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch(null);
  };

  useEffect(() => {
    const jobReviewStatus =
      selectedReviewTab === JobReviewTabStatus.ALL ? null : (selectedReviewTab as unknown as JobReviewStatus);
    setQueryParams(prev => ({
      ...prev,
      jobReviewStatus
    }));
  }, [selectedReviewTab]);

  const onTabChange = status => {
    dispatch(setSelectedReviewTab(status));
    setSelectedJobs([]);

    // Reset filter states directly
    setSelectedCustomers([]);
    setSelectedMerchants([]);
    setLeadSources([]);
    setAmountFilter({
      filterOperation: AmountFilterOperation.ALL,
      minAmount: null,
      maxAmount: null
    });
    setScheduleStartDate(null);
    setScheduleEndDate(null);
    setExternalCompanies([]);
    setServiceAreas([]);
    setTags([]);

    // Reset search state
    setSearchText('');
    if (searchType !== jobSearchTypes[0]) {
      setSearchType(jobSearchTypes[0]);
    }
    initiateSearch(null);
  };

  const onPageChange = (pageNumber: number) => {
    fetchData(pageNumber);
  };

  const handleJobCheckboxSelect = (job: PayRunMemberJob) => {
    setSelectedJobs(prev => {
      const isJobSelected = prev.some(j => j.jobId === job.jobId);
      if (isJobSelected) {
        return prev.filter(j => j.jobId !== job.jobId);
      }
      return [...prev, job];
    });
  };

  const handleSelectAllJobs = () => {
    if (selectedJobs?.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs);
    }
  };

  const handleShowConfirmModal = (status: string) => {
    setStatusToConfirm(status);
    setShowConfirmModal(true);
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedJobs.length === 0 || !statusToConfirm) return;
    try {
      await updateBulkJobStatus({
        teamId: currentTeam?.id.toString(),
        payRunId,
        merchantId,
        status: statusToConfirm,
        jobIds: selectedJobs.map(job => job.jobId)
      });
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunJobStatusChange, {
        payRunId,
        merchantId,
        status: statusToConfirm,
        jobCount: selectedJobs.length
      });

      // Clear selections after successful update
      setSelectedJobs([]);
    } catch (error) {
      //error handling
    } finally {
      setShowConfirmModal(false);
    }
  };

  const getModalTitle = () => {
    if (!statusToConfirm) return '';
    return `${getJobReviewStatusMap(statusToConfirm).label} jobs`;
  };

  // Expose job navigation functions
  useEffect(() => {
    if (setJobNavigationFunctions && jobs.length > 0) {
      setJobNavigationFunctions({
        getNextJob: currentJobId => {
          const currentIndex = jobs.findIndex(job => job.jobId === currentJobId);
          if (currentIndex !== -1 && currentIndex < jobs.length - 1) {
            return {
              jobId: jobs[currentIndex + 1].jobId,
              status: jobs[currentIndex + 1].status
            };
          }
          return { jobId: '', status: '' };
        },
        getPreviousJob: currentJobId => {
          const currentIndex = jobs.findIndex(job => job.jobId === currentJobId);
          if (currentIndex > 0) {
            return {
              jobId: jobs[currentIndex - 1].jobId,
              status: jobs[currentIndex - 1].status
            };
          }
          return { jobId: '', status: '' };
        }
      });
    }
  }, [jobs, setJobNavigationFunctions]);

  // Individual filter handlers
  const handleCustomersChange = (customers: string[]) => {
    setSelectedCustomers(customers);
    setQueryParams(prev => ({ ...prev, customerIds: customers.length > 0 ? customers.join(',') : null }));
  };

  const handleMerchantsChange = (merchants: string[]) => {
    setSelectedMerchants(merchants);
    setQueryParams(prev => ({ ...prev, createdBy: merchants.length > 0 ? merchants.join(',') : null }));
  };

  const handleLeadSourcesChange = (sources: string[]) => {
    setLeadSources(sources);
    setQueryParams(prev => ({ ...prev, leadSource: sources.length > 0 ? sources.join(',') : null }));
  };

  const handleAmountFilterChange = (filter: {
    filterOperation: AmountFilterOperation;
    minAmount: number | null;
    maxAmount: number | null;
  }) => {
    const { filterOperation, minAmount, maxAmount } = filter;
    let amount = `${filterOperation}:${minAmount}`;
    if (filterOperation === AmountFilterOperation.ALL) amount = '';
    if (filterOperation === AmountFilterOperation.BW) amount = `${filterOperation}:${minAmount}:${maxAmount}`;
    setAmountFilter(filter);
    setQueryParams(prev => ({
      ...prev,
      amount
    }));
  };

  const handleScheduleStartDateChange = (date: string | null) => {
    setScheduleStartDate(date);
    setQueryParams(prev => ({ ...prev, scheduledFrom: DateUtils.startDateToUTCString(date) }));
  };

  const handleScheduleEndDateChange = (date: string | null) => {
    setScheduleEndDate(date);
    setQueryParams(prev => ({ ...prev, scheduledTo: DateUtils.endDateToUTCString(date) }));
  };

  const handleExternalCompaniesChange = (companies: string[]) => {
    setExternalCompanies(companies);
    setQueryParams(prev => ({ ...prev, externalCompany: companies.length > 0 ? companies.join(',') : null }));
  };

  const handleTagsChange = (tagsArray: string[]) => {
    setTags(tagsArray);
    const isNoTagsIncluded = tagsArray.includes(NO_TAGS_FILTER_ID);
    const tagsToSet = isNoTagsIncluded ? tagsArray.filter(tag => tag !== NO_TAGS_FILTER_ID) : tagsArray;
    setQueryParams(prev => ({
      ...prev,
      tags: tagsToSet?.length > 0 ? tagsToSet.join(',') : null,
      ...(isNoTagsIncluded ? { noTags: isNoTagsIncluded } : { noTags: null })
    }));
  };

  const handleServiceAreasChange = (areas: string[]) => {
    setServiceAreas(areas);
    setQueryParams(prev => ({ ...prev, serviceAreas: areas.length > 0 ? areas.join(',') : null }));
  };

  const handleInvoiceStatusChange = (status: string[]) => {
    setInvoiceStatus(status);
    setQueryParams(prev => ({ ...prev, invoiceStatus: status.length > 0 ? status.join(',') : null }));
  };

  const handleEstimateStatusChange = (status: string[]) => {
    setEstimateStatus(status);
    setQueryParams(prev => ({ ...prev, estimateStatus: status.length > 0 ? status.join(',') : null }));
  };

  const handleCreatedStartDateChange = (date: string | null) => {
    setCreatedStartDate(date);
    setQueryParams(prev => ({ ...prev, createdFrom: DateUtils.startDateToUTCString(date) }));
  };

  const handleCreatedEndDateChange = (date: string | null) => {
    setCreatedEndDate(date);
    setQueryParams(prev => ({ ...prev, createdTo: DateUtils.endDateToUTCString(date) }));
  };

  const handleSortChange = (newSortBy: string, newSortOrder: string) => {
    setQueryParams(prev => ({
      ...prev,
      sortBy: newSortBy,
      sortOrder: newSortOrder
    }));
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunSortByUse, {
      payRunId,
      table: 'pay-run-confirm-jobs',
      sortBy: newSortBy,
      sortOrder: newSortOrder
    });
  };
  const handleSaveColumns = async (updatedColumns: ColumnConfig[]) => {
    if (!currentTeam?.id) return;
    try {
      await saveCustomColumns({
        teamId: currentTeam.id,
        payload: {
          feature: CustomizeColumnsSource.PAYRUN_MEMBER_REVIEW,
          columnInfo: updatedColumns
        }
      });
      logAnalyticEvent(PayRunsCleverTapEvents.webCustomizeColumnsSaved, {
        source: CustomizeColumnsSource.PAYRUN_MEMBER_REVIEW
      });
    } finally {
      setShowCustomizeColumns(false);
    }
  };

  const handleExportClick = async () => {
    if (!currentTeam?.id || !payRunId || !merchantId) return;

    try {
      // Prepare export parameters by excluding limit and page from query params
      const { search, ...restQueryParams } = queryParams;
      const isSearchPresent = search !== null && search !== '';

      await exportPayRunMemberJobs({
        teamId: currentTeam.id.toString(),
        payRunId,
        merchantId,
        ...restQueryParams,
        [searchType.type]: isSearchPresent ? search?.trim() : null,
        jobReviewStatus: queryParams.jobReviewStatus ? [queryParams.jobReviewStatus] : []
      });
    } catch (error) {
      // Error handling is managed by the API layer
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <JobReviewStatusTabs onChange={onTabChange} statusCounts={statusCounts} />
      <div id="job-review-filters" className="flex flex-col gap-4 px-[30px]">
        <JobReviewFilters
          selectedCustomers={selectedCustomers}
          setSelectedCustomers={handleCustomersChange}
          selectedMerchants={selectedMerchants}
          setSelectedMerchants={handleMerchantsChange}
          leadSources={leadSources}
          setLeadSources={handleLeadSourcesChange}
          amountFilter={amountFilter}
          setAmountFilter={handleAmountFilterChange}
          scheduleStartDate={scheduleStartDate}
          setScheduleStartDate={handleScheduleStartDateChange}
          scheduleEndDate={scheduleEndDate}
          setScheduleEndDate={handleScheduleEndDateChange}
          externalCompanies={externalCompanies}
          setExternalCompanies={handleExternalCompaniesChange}
          selectedTags={tags}
          setSelectedTags={handleTagsChange}
          selectedServiceAreas={serviceAreas}
          setSelectedServiceAreas={handleServiceAreasChange}
          searchText={searchText}
          onSearchTermChange={onSearchTermChange}
          onClearSearch={onClearSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          createdStartDate={createdStartDate}
          setCreatedStartDate={handleCreatedStartDateChange}
          createdEndDate={createdEndDate}
          setCreatedEndDate={handleCreatedEndDateChange}
          invoiceStatus={invoiceStatus}
          setInvoiceStatus={handleInvoiceStatusChange}
          estimateStatus={estimateStatus}
          setEstimateStatus={handleEstimateStatusChange}
        />
      </div>
      {isFetchingJobs && <Loader />}
      {!isFetchingJobs && jobs.length === 0 && <EmptyStateJobReview />}
      {!isFetchingJobs && jobs.length > 0 && (
        <div>
          {isActiveFSMPlusPlan && (
            <JobReviewTableActions
              selectedJobs={selectedJobs}
              onClearSelection={() => setSelectedJobs([])}
              onExportClick={handleExportClick}
              onEditTableClick={() => setShowCustomizeColumns(true)}
              onShowConfirmModal={handleShowConfirmModal}
              hasDraftAdjustment={selectedJobs?.some(job => job.hasDraftAdjustment)}
            />
          )}
          <JobReviewTable
            jobs={jobs}
            page={page}
            totalCount={totalCount}
            onPageChange={onPageChange}
            selectedJobs={selectedJobs}
            onJobCheckboxSelect={handleJobCheckboxSelect}
            onSelectAll={handleSelectAllJobs}
            fetchData={fetchData}
            onSortChange={handleSortChange}
            currentSortBy={queryParams.sortBy}
            currentSortOrder={queryParams.sortOrder}
            columnInfo={columnConfig}
            stickyHorizontalScrollbar={{ bottomOffsetPx: 80 }}
            highlightedRowId={jobId ?? undefined}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <GenericModal
        showModal={showConfirmModal}
        isActionInProgress={isActionInProgress}
        CloseIcon={CrossIconvector}
        title={getModalTitle()}
        subTitle={getConfirmMoveJobsSubtitle(selectedJobs.length, getJobReviewStatusMap(statusToConfirm).label)}
        primaryBtnLabel="Yes, move"
        onPrimaryBtnClick={handleBulkStatusUpdate}
        secondaryBtnLabel="No, Cancel"
        onSecondaryBtnClick={() => setShowConfirmModal(false)}
        primaryBtnId="confirm-status-change"
        secondaryBtnId="cancel-status-change"
        containerWidth="343px"
        className="px-4 py-[30px]"
        titleStyle="!text-xl font-semibold text-headingGray mb-2"
        subTitleStyle="text-sbase font-semibold text-headingGray text-center mb-4"
        onCloseIconClick={() => setShowConfirmModal(false)}
      />
      {showCustomizeColumns && (
        <CustomizeColumnsModal
          open={showCustomizeColumns}
          onClose={() => setShowCustomizeColumns(false)}
          columns={columnConfig}
          onSave={handleSaveColumns}
          reorderableStartIndex={1}
          isLoading={isSavingColumns}
        />
      )}
    </div>
  );
};

export default ConfirmJobsTab;
